import React from 'react';
import { formatDate } from '../../calendar.model';
import { dateFormatSettings, timeFormatSettings } from '../../../settings/settings.consts';
import { NONE, CSS } from './weight-and-balance-manifest.constants';
import { getWeightAndBalanceFields } from '../weight-and-balance.helpers';
import { ROW_GROUP } from '../weight-and-balance.consts';
import { EventTime } from '../../event/event-time';
import { resolveUserFormat } from '../../../common/user-formats/userFormats';

import { FuelData } from './components/fuel';
import { Hobbs } from './components/hobbs';
import { ParkFly } from './components/park-fly';
import { Sign } from './components/sign';
import { Table } from './components/table';
import { Location } from './components/location';
import { ActivityType } from './components/activity-type';
import { PassTable } from './components/pass-table';
import { PassBlock } from './components/pass-block';
import { PerformanceFactors } from './components/performance-factors';
import { PerformanceSunshine } from './components/performance-sunshine';
import { PassMap } from './components/pass-map';
import { Comments } from './components/comments';
import { FratQuestionnaire, isFratOrg } from './components/frat-questionnaire';

export const LIMIT_MSG = {
  cg: 'CG limit',
  gw: 'Max G.W.',
  tank: 'Tank full'
};

const Cell = ({ title, text }) => (
  <div>
    <span className="title">{title}:</span>
    &nbsp;
    <span>{text}</span>
  </div>
);

const toSafeNumber = value => {
  const probablyNumber = Number(value).toFixed(0);
  return isNaN(+probablyNumber) ? 0 : +probablyNumber;
};

const getAircraft = event => {
  // Rem38 M7-ext — `event.resources` may be absent on some shapes; guard the
  // `.find` so a missing array can't throw.
  return (
    (Array.isArray(event?.resources) ? event.resources : []).find(resource => {
      return resource.unique === 'aircraft';
    }) || {}
  );
};

const calculateLimitationMessage = (event, weightAndBalance, fuelTable) => {
  const aircraft = getAircraft(event);

  const { weightAndBalanceTable } = weightAndBalance;

  const { slidingFuelTankGraph, staticFuelTank, maxTakeoffWeight, slidingFuelTankId } = aircraft;

  if (!slidingFuelTankGraph && !staticFuelTank && !maxTakeoffWeight && !slidingFuelTankId) {
    return LIMIT_MSG.cg;
  }

  // If any fuel tank is 100% load;
  // or it's sliding tank loaded to max
  // or it's static tank loaded to ma

  // Rem38 M7-ext v3 — defensive `?.` on the W&B-table / fuel-table chains
  // (`toSafeNumber` already coerces undefined → 0) so a missing row/key can't
  // throw a TypeError.
  let isTankIsFull = toSafeNumber(fuelTable?.max?.percent) === 100;

  const currentFuelWeight = toSafeNumber(weightAndBalanceTable?.['Fuel Weight']?.Weight);
  const maxAvailableFuel = toSafeNumber(fuelTable?.max?.lbs);
  const grossWeightWithCurFuel = toSafeNumber(weightAndBalanceTable?.['Takeoff Weight & CG']?.Weight);
  const aircraftTakeoffWeight = toSafeNumber(maxTakeoffWeight);

  // Current aircraft weight + max fuel it can carry
  const grossWeight = grossWeightWithCurFuel - currentFuelWeight + maxAvailableFuel;

  const isAircraftWeightCloseToLimit =
    grossWeight >= aircraftTakeoffWeight - 10 && grossWeight <= aircraftTakeoffWeight;

  if (isTankIsFull) {
    return LIMIT_MSG.tank;
  } else if (isAircraftWeightCloseToLimit) {
    return LIMIT_MSG.gw;
  } else {
    return LIMIT_MSG.cg;
  }
};

const generatePassBlockOptions = (passList, isPilotRight, reverseManifestSeatNumbers) => {
  let passengerCount = 0;
  let crewCount = 0;

  passList.forEach(row => {
    row.forEach(passenger => {
      if (passenger !== null && passenger.type !== 'Pilot' && passenger.type !== 'SIC Pilot') {
        ++passengerCount;
      } else if (passenger !== null) {
        ++crewCount;
      }
    });
  });

  const processedList = passList.filter(row => row.length);

  return {
    passengerCount,
    passList: processedList,
    crewCount,
    isPilotRight,
    reverseManifestSeatNumbers
  };
};

export const WeightAndBalanceManifest = props => {
  const {
    event,
    weightAndBalance,
    user,
    resources,
    imageBlob,
    envelopeImageBlob,
    activityTypes,
    reverseManifestSeatNumbers,
    formats
  } = props;

  const showPiCSign = true;
  let showPassBriefing = true;
  const showActivityType = true;
  let showWBLimits = true;
  let showLocation = true;
  let showHobbs = true;
  let showParkFly = true;
  let showPerformanceFactors = false;
  let passMap = false;
  let passBlock;

  let fuelOptions = {
    takeOff: true,
    landing: true,
    maxFuel: true,
    minFuel: true,
    reserveFuel: true
  };

  const orgName = event.organizationUnique;

  const isBlueHawaii = orgName === 'blue-hawaiian-helicopters-hilo';
  // Org-gated HOT FRAT questionnaire (Jack Harter) — printed under HIGE/HOGE/INIT.
  const showFrat = isFratOrg(orgName);

  // TODO Feature flags, remove after prod deploy
  const showParadiseManifest = localStorage.getItem('showParadiseManifest') === 'true';
  const showHarterManifest = localStorage.getItem('showHarterManifest') === 'true';
  const showBHHManifest = localStorage.getItem('showBHHManifest') === 'true';
  const showSunshineManifest = localStorage.getItem('showSunshineManifest') === 'true';
  const showAirKauaiManifest = localStorage.getItem('showAirKauaiHelicopters') === 'true';
  // Rem38 M7 — Symfony event detail uses `aviationResourceId` (not the legacy
  // `aircraftResourceID`), and the `resources` param may be an id-keyed map OR an
  // array. The old `resources[event.aircraftResourceID].pilotRight` threw
  // `Cannot read properties of undefined (reading 'pilotRight')` for Symfony
  // events. Resolve defensively from `resources` first, then fall back to the
  // event-local aircraft shapes; default false on miss (e.g. maintenance event
  // with no aircraft) so the manifest never throws.
  const aircraftResourceId = event.aviationResourceId ?? event.aircraftResourceID;
  const aircraftResource = Array.isArray(resources)
    ? resources.find(r => String(r?.id) === String(aircraftResourceId))
    : (aircraftResourceId != null ? resources?.[aircraftResourceId] : undefined);
  const aircraftFromEvent =
    event?.eventResources?.[0]?.resource?.aircraft ??
    (Array.isArray(event?.resources)
      ? event.resources.find(r => r?.typeName === 'Aircraft')
      : undefined);
  const pilotRightRaw = aircraftResource?.pilotRight ?? aircraftFromEvent?.pilotRight;
  const isPilotRight = pilotRightRaw === '1' || pilotRightRaw === true || pilotRightRaw === 1;
  const isSunshineHelicopters = orgName === 'sunshine-helicopters' || showSunshineManifest;
  const isAirKauaiHelicopters = orgName === 'air-kauai-helicopters' || showAirKauaiManifest;

  const seatTableOrgs = [
    'paradise-helicopters',
    'jack-harter-helicopters',
    'sunshine-helicopters',
    'airventures-hawaii',
    'air-kauai-helicopters'
  ];

  const showSeatTable =
    seatTableOrgs.includes(orgName) ||
    showParadiseManifest ||
    showHarterManifest ||
    showSunshineManifest ||
    showAirKauaiManifest;

  // Rem38 M7-ext — Symfony event detail has NO `timeZone` map / `timeZoneID`
  // (legacy: `event.timeZone` = `{ [tzId]: TimeZoneDto }`, `event.timeZoneID` =
  // string key). The old `event.timeZone[event.timeZoneID]` threw
  // `Cannot read properties of undefined (reading 'undefined')` for Symfony
  // events. Resolve defensively to an OBJECT with a numeric `.offset` (consumed
  // by `<EventTime timezone={timezone}>` → `timezone.offset`), so neither a
  // throw nor a `NaN` offset can occur:
  //   1. legacy map value (exact legacy behavior, real offset)
  //   2. Symfony `locationTimeZone` TimeZoneDto object (real offset)
  //   3. UTC-shaped fallback (renders; offset 0) keyed by the IANA id.
  const legacyTz =
    event?.timeZone && event?.timeZoneID != null ? event.timeZone[event.timeZoneID] : null;
  const symfonyTz =
    event?.locationTimeZone && typeof event.locationTimeZone === 'object'
      ? event.locationTimeZone
      : event?.location?.timeZone && typeof event.location.timeZone === 'object'
        ? event.location.timeZone
        : null;
  const tzId =
    legacyTz?.id ??
    symfonyTz?.id ??
    event?.locationTimezoneId ??
    event?.location?.timeZoneId ??
    event?.timeZoneID ??
    'UTC';
  const timezone = legacyTz ||
    symfonyTz || { id: tzId, offset: 0, offsetString: '+00:00', dst: false, abbr: 'UTC' };
  const fields = getWeightAndBalanceFields(
    weightAndBalance.weightAndBalanceTable,
    weightAndBalance.passengers,
    weightAndBalance.rows,
    reverseManifestSeatNumbers
  );

  const spacedFields = fields.reduce((acc, field) => {
    const prevField = acc[acc.length - 1];

    const hasDifferentGroup = prevField && prevField.group !== field.group;
    const isAfterRow = prevField && prevField.group === ROW_GROUP;

    if (isAfterRow || (hasDifferentGroup && field.group !== ROW_GROUP)) {
      acc.push(null);
    }

    if (field.status && !field.title) {
      return acc;
    }

    return acc.concat(field);
  }, []);

  const activityType =
    showActivityType && event.activityType && activityTypes
      ? activityTypes[event.activityType]
      : undefined;

  const pilotName = event.pilotName
    ? [event.pilotName, event.pilotMiddlename, event.pilotSurname].filter(Boolean).join(' ')
    : NONE;
  const sicPilot =
    event.secondInComand === '1' && event.sicPilotName
      ? [event.sicPilotName, event.sicPilotMiddlename, event.sicPilotSurname]
          .filter(Boolean)
          .join(' ')
      : NONE;
  const instructorName =
    parseInt(event.instructorID, 10) > 0 && event.instructorName
      ? [event.instructorName, event.instructorMiddlename, event.instructorSurname]
          .filter(Boolean)
          .join(' ')
      : NONE;

  // TODO temp solution till move to user calendar settings
  if (showSeatTable) {
    passBlock = generatePassBlockOptions(
      weightAndBalance.passengers,
      isPilotRight,
      reverseManifestSeatNumbers
    );
    showPerformanceFactors = true;
    showLocation = false;
    showParkFly = false;
    showHobbs = false;
    showParkFly = false;
  }

  if (isSunshineHelicopters || isAirKauaiHelicopters) {
    showPerformanceFactors = false;
  }

  if (isAirKauaiHelicopters) {
    showWBLimits = false;
    showPassBriefing = false;
  }

  const fuel = weightAndBalance.fuelWeightTable;
  // Rem38 M7-ext — guard the array + the `.find` miss (`?.name`) so a missing
  // Aircraft resource can't throw; default to empty string.
  const aircraftName =
    (Array.isArray(event?.resources) ? event.resources : []).find(
      ({ typeName }) => typeName === 'Aircraft'
    )?.name || '';
  // Rem38 M7-ext v3 — `user.settings` is undefined on the Symfony Redux user
  // shape → `user.settings.date` threw `Cannot read properties of undefined
  // (reading 'date')`. Optional-chain + sensible defaults.
  const dateFmt =
    dateFormatSettings[user?.settings?.date] ||
    resolveUserFormat('twigDateFormat', formats) ||
    'j M, Y';
  const timeFmt =
    timeFormatSettings[user?.settings?.time] ||
    resolveUserFormat('twigTimeFormat', formats) ||
    'H:i';
  const runDate = formatDate(
    Math.floor(Date.now() / 1000),
    undefined,
    `${dateFmt} ${timeFmt}`,
    false,
    false
  );

  const dueToMessage = calculateLimitationMessage(event, weightAndBalance, fuel);

  if (isAirKauaiHelicopters) {
    const pass = [];
    const excludedLines = ['Taxi & Run-up Fuel Burn', 'Ramp Weight & CG'];

    const trimmedSpacedFields = spacedFields.reduce((acc, field) => {
      if (field !== null && field.group === 'passenger') {
        pass.push(field);
      } else {
        if (field && !excludedLines.includes(field.id)) {
          acc.push(field);
        }
      }
      return acc;
    }, []);

    fuelOptions = {
      ...fuelOptions,
      takeOff: false,
      landing: false
    };

    passBlock = generatePassBlockOptions(
      weightAndBalance.passengers,
      isPilotRight,
      reverseManifestSeatNumbers
    );

    const manifestBody = (
      <React.Fragment>
        <div className="flex-row">
          {/* LEFT COLUMN */}
          <div className="flex-column">
            {activityType && <ActivityType activityType={activityType} />}
            <EventTime title="Start time" user={user} timezone={timezone} date={event.startTime} formats={formats} />
            {showLocation && <Location event={event} timezone={timezone} />}
            <Cell title="Pilot" text={pilotName} />
            <Cell title="Aircraft" text={aircraftName} />

            <Cell title="Run by" text={user.name} />
            <Cell title="Run date" text={runDate} />
            <PassMap seatsData={passBlock} />

            {showPiCSign && <Sign text="PIC Sign:" isDoubleSided={true} />}
            {showWBLimits && <Sign text="I have found this W&B to be within limits:" />}
            {showPassBriefing && <Sign text="Passenger briefing given by:" />}
            {showParkFly && <ParkFly />}
            {showHobbs && <Hobbs />}
            {fuel && (
              <FuelData
                fuel={fuel}
                fuelOptions={fuelOptions}
                dueToMessage={dueToMessage}
                isOneRow={true}
              />
            )}
            <div className="table-block">
              <Table spacedFields={trimmedSpacedFields} weightAndBalance={weightAndBalance} />
            </div>
            <div className="image">
              {imageBlob && (
                <img width={290} src={imageBlob} alt="Weight and Balance image" />
              )}
              {envelopeImageBlob && !showFrat && (
                <img
                  width={290}
                  src={envelopeImageBlob}
                  alt="Weight and Balance envelope chart"
                />
              )}
            </div>
            {showPerformanceFactors && <PerformanceFactors />}
            {isSunshineHelicopters && <PerformanceSunshine />}
          </div>
        </div>
      </React.Fragment>
    );

    return (
      <section className={"toPrint" + (showFrat ? " manifest-compact" : "")}>
        <style children={CSS} />
        <h1 className="text-center" children={event.organizationName} />
        {!isAirKauaiHelicopters ? (
          { manifestBody }
        ) : (
          <div className="flex-row">
            <div className="flex-column" children={manifestBody} />
            <div className="flex-column" children={manifestBody} />
          </div>
        )}
      </section>
    );
  }

  if (isBlueHawaii || showBHHManifest || showSeatTable) {
    const pass = [];
    const excludedLines = ['Taxi & Run-up Fuel Burn', 'Ramp Weight & CG'];

    const trimmedSpacedFields = spacedFields.reduce((acc, field) => {
      if (field !== null && field.group === 'passenger') {
        pass.push(field);
      } else {
        if (field && !excludedLines.includes(field.id)) {
          acc.push(field);
        }
      }
      return acc;
    }, []);

    fuelOptions = {
      ...fuelOptions,
      takeOff: false,
      landing: false
    };

    return (
      <section className={"toPrint" + (showFrat ? " manifest-compact" : "")}>
        <style>{CSS}</style>
        <h1 className="text-center">{event.organizationName}</h1>

        <div className="flex-row">
          {/* LEFT COLUMN */}
          <div className="flex-column flex-10">
            {activityType && <ActivityType activityType={activityType} />}
            <EventTime title="Start time" user={user} timezone={timezone} date={event.startTime} formats={formats} />

            {passBlock ? <PassBlock {...passBlock} /> : <PassTable passFields={pass} />}
            {passMap && <PassMap seatsData={pass} />}
          </div>

          {/* RIGHT COLUMN */}
          <div className="flex-column flex-7">
            {showLocation && <Location event={event} timezone={timezone} />}
            <Cell title="Pilot" text={pilotName} />
            <Cell title="Aircraft" text={aircraftName} />

            <Cell title="Run by" text={user.name} />
            <Cell title="Run date" text={runDate} />
            {showPiCSign && <Sign text="PIC Sign:" />}
            {showWBLimits && <Sign text="I have found this W&B to be within limits:" />}
            {showPassBriefing && <Sign text="Passenger briefing given by:" />}
            {showParkFly && <ParkFly />}
            {showHobbs && <Hobbs />}
          </div>
        </div>
        {fuel && <FuelData fuel={fuel} fuelOptions={fuelOptions} dueToMessage={dueToMessage} />}
        <div className="spacing"></div>
        {isSunshineHelicopters && <Comments comments={event.comments} />}
        <div className="flex-row top-indent">
          <div className="flex-column flex-10">
            <div className="table-block">
              <Table spacedFields={trimmedSpacedFields} weightAndBalance={weightAndBalance} />
            </div>
          </div>
          <div className="flex-column flex-8">
            <div className="image">
              {imageBlob && (
                <img width={290} src={imageBlob} alt="Weight and Balance image" />
              )}
              {envelopeImageBlob && !showFrat && (
                <img
                  width={290}
                  src={envelopeImageBlob}
                  alt="Weight and Balance envelope chart"
                />
              )}
            </div>
            {showPerformanceFactors && <PerformanceFactors />}
            {showFrat && <FratQuestionnaire />}
            {isSunshineHelicopters && <PerformanceSunshine />}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={"toPrint" + (showFrat ? " manifest-compact" : "")}>
      <style>{CSS}</style>
      <h1 className="text-center">{event.organizationName}</h1>

      <div className="flex-row">
        {/* LEFT COLUMN */}
        <div className="flex-column flex-half">
          <Cell title="Report" text="Weight and Balance" />
          <Cell title="Aircraft" text={aircraftName} />
          <hr />
          {showLocation && <Location event={event} timezone={timezone} />}
          {activityType && <ActivityType activityType={activityType} />}
          <EventTime title="Start time" user={user} timezone={timezone} date={event.startTime} formats={formats} />
          <EventTime title="End time" user={user} timezone={timezone} date={event.finishTime} formats={formats} />
          <hr />
          <Cell title="Pilot" text={pilotName} />
          <Cell title="SIC Pilot" text={sicPilot} />
          <Cell title="Instructor" text={instructorName} />
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex-column flex-half">
          <Cell title="Run by" text={user.name} />
          <Cell title="Run date" text={runDate} />
          {showPiCSign && <Sign text="PIC Sign:" />}
          {showWBLimits && <Sign text="I have found this W&B to be within limits:" />}
          {showPassBriefing && <Sign text="Passenger briefing given by:" />}
          {showParkFly && <ParkFly />}
          {showHobbs && <Hobbs />}
        </div>
      </div>

      {fuel && <FuelData fuel={fuel} fuelOptions={fuelOptions} dueToMessage={dueToMessage} />}

      <div className="flex-row top-indent">
        <div className="flex-column flex-10">
          <div className="table-block">
            <Table spacedFields={spacedFields} weightAndBalance={weightAndBalance} />
          </div>
        </div>
        <div className="flex-column flex-8">
          <div className="image">
            {imageBlob && (
              <img width={290} src={imageBlob} alt="Weight and Balance image" />
            )}
            {envelopeImageBlob && !showFrat && (
              <img
                width={290}
                src={envelopeImageBlob}
                alt="Weight and Balance envelope chart"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
